import { createFactory, memo } from 'react'
import React from 'react'
import ReactDOM from 'react-dom'
import CsgModalConfirmation from 'components/organisms/ModalConfirmation'
import PropTypes from 'prop-types'
import * as R from 'ramda'
import dateFnsFormat from 'date-fns/format'
import { withProps, branch, withState } from 'recompose'

export const redirectTo = defaultPath => ({ history }) => path => history.push(path || defaultPath)

const getMessageFromException = errorMessage => {
  var result = errorMessage.match(/.*Exception: (.*)/i)
  return result ? result[1] : false
}

const getMessageFromGrupoValidacaoException = errorMessage => {
  var result = errorMessage.includes('Grupo de Validação:')
  return result ? errorMessage : false
}

export const getParamKeyByLayoutType = {
  CONSIGNACAO_MENSAL: () => 'parametrosConsignacaoMensal',
  CONFERENCIA_FECHAMENTO: () => 'parametrosFechamento'
}

export const hasValue = R.compose(R.not, R.either(R.isNil, R.isEmpty))

export const allHasValue = R.all(hasValue)

export const translateRestError = (t, key) => {
  if (!key) {
    return t(`rest_errors.default`)
  }

  const hasErrorException = getMessageFromException(key)
  if (hasErrorException) return hasErrorException

  const hasGrupoValidacaoErrorException = getMessageFromGrupoValidacaoException(key)
  if (hasGrupoValidacaoErrorException) return hasGrupoValidacaoErrorException

  const translationResult = t(`rest_errors.${key}`)
  const translationNotFound = translationResult.includes('rest_errors')

  return translationNotFound ? key : translationResult
}

export const withRestErrorHandler = () => Component => {
  const factory = createFactory(Component)

  return memo(props =>
    factory({
      ...props,
      tRestError: key => translateRestError(props.t, key)
    })
  )
}

export const getErrorMessageFromBlobResponse = async response => {
  try {
    const json = JSON.parse(await response.data.text())
    return json.message
  } catch (_) {
    return undefined
  }
}

export const getRestListData = response => {
  const data = R.when(R.has('data'), R.prop('data'))
  const embedded = R.when(R.has('_embedded'), R.prop('_embedded'))
  const getFirstKey = object => R.compose(R.prop(R.__, object), R.head, R.keys)(object)

  if (!R.has('data', response) && !R.has('_embedded', response)) {
    return []
  }

  return R.compose(getFirstKey, embedded, data)(response)
}

export const colors = [
  'common',
  'background',
  'primary',
  'secondary',
  'info',
  'success',
  'purple',
  'error',
  'warning',
  'text',
  'grey'
]

export const colorPropType = PropTypes.oneOf(colors)

export const moneyToFloat = R.compose(
  parseFloat,
  val => `${val.substr(0, val.length - 2)}.${val.substr(-2)}`,
  R.replace(/[^\d]/g, '')
)

export const moneyToBigDecimal = val => {
  if (val == null || val === '') return 0
  const clean = String(val).replace(/[^0-9.,]/g, '')
  if (clean.includes(',') || clean.includes('.')) {
    return parseFloat(clean.replace(',', '.'))
  }
  return parseFloat(clean)
}

export const formatCurrency = value =>
  new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(value)

export const formatNumberWithCheckDigit = (number, checkDigit) =>
  number == null || number === '' ? '' : `${number}${checkDigit == null || checkDigit === '' ? '' : `-${checkDigit}`}`

export const stringToNumber = R.compose(parseFloat, R.replace(',', '.'))

export const parseDecimalNumber = val => {
  if (val == null || val === '') return 0
  const str = String(val)
  return Number(str.includes(',') ? str.replace(/\./g, '').replace(',', '.') : str)
}

export const unquoteString = value => (typeof value === 'string' ? value.replace(/^"|"$/g, '') : value)

export const numberToString = val => {
  return parseFloat(val)
    .toFixed(2)
    .replace('.', ',')
    .toString()
}

export const defer = (fn, time = 0) => setTimeout(fn, time)

export const dateToFolha = date => parseInt(dateFnsFormat(date, 'yyyyMM'))

export const formatDatePtBr = date => {
  const day = String(date.getDate()).padStart(2, '0')
  const month = String(date.getMonth() + 1).padStart(2, '0')
  const year = date.getFullYear()

  return `${day}/${month}/${year}`
}

export const parseDate = dateValue => {
  if (!dateValue) return null

  if (dateValue instanceof Date && !Number.isNaN(dateValue.getTime())) {
    return dateValue
  }

  if (typeof dateValue === 'string') {
    const isPtBrFormat = /^\d{2}\/\d{2}\/\d{4}$/.test(dateValue)

    if (isPtBrFormat) {
      const [day, month, year] = dateValue.split('/').map(Number)
      const parsedDate = new Date(year, month - 1, day)

      const isStrictValidDate =
        !Number.isNaN(parsedDate.getTime()) &&
        parsedDate.getDate() === day &&
        parsedDate.getMonth() === month - 1 &&
        parsedDate.getFullYear() === year

      if (isStrictValidDate) {
        return parsedDate
      }
    }

    const parsedDate = new Date(dateValue)
    if (!Number.isNaN(parsedDate.getTime())) {
      return parsedDate
    }
  }

  return null
}

export const folhaToDate = folha => {
  const year = folha.toString().substr(0, 4)
  const month = folha.toString().substr(-2)

  return new Date(year, month - 1, 1, 12)
}

export const titlealize = str => str.toLowerCase().replace(/(?:^|\s)\S/g, val => val.toUpperCase())

export const getFileExtension = filename => filename.split('.').pop()

export const formatExtensionFile = fileName => {
  const type = getFileExtension(fileName)
  return formatExtension(type)
}

export const formatExtension = extension => {
  if (extension !== null) {
    return `.${extension.toLowerCase()}`
  }
  return
}

export const getFileExtensionByBase64 = base64 => {
  const char = base64.charAt(0)

  const extension = {
    '/': () => 'jpg',
    i: () => 'png',
    J: () => 'pdf',
    Default: () => 'txt'
  }

  return (extension[char] || extension['Default'])()
}

export const downloadFile = (filename, base64) => {
  const type = getFileExtensionByBase64(base64)
  download(filename, base64, type)
}

export const downloadFileWithExtension = (filename, base64, extension) => {
  download(filename, base64, extension)
}

export const download = (name, base64, type) => {
  const url = `data:application/${type};base64,${base64}`
  const fileName = `${name}.${type}`

  downloadLink(fileName, url)
}

export const downloadPDF = (name, pdfBase64) => {
  const url = `data:application/pdf;base64,${pdfBase64}`
  const fileName = `${name}.pdf`

  downloadLink(fileName, url)
}

export const downloadLink = (name, url) => {
  const element = document.createElement('a')

  element.href = url
  element.download = name
  element.click()
}

export const verifyOrRules = R.curry((rules, authorities = []) => {
  const userRules = R.map(rule => (rule.authority ? rule.authority : rule), authorities)
  const arrRules = Array.isArray(rules) ? rules : [rules]

  return !!R.intersection(userRules, arrRules).length
})

export const verifySessionIsOr = R.curry((types, session = {}) => {
  const arrTypes = Array.isArray(types) ? types : [types]
  return arrTypes.filter(type => session[type]).length
})

export const verifySessionIs = R.curry((type, session = {}) => session[type])

export const verifyHasRules = R.curry((rules, authorities = []) => {
  const userRules = R.map(rule => (rule.authority ? rule.authority : rule), authorities)
  const arrRules = Array.isArray(rules) ? rules : [rules]

  return R.intersection(userRules, arrRules).length === arrRules.length
})

export const verifyRuleUpdateOrView = R.curry((rule, authorities, { view, update }) => {
  if (verifyHasRules(rule.concat('.EDIT'), authorities)) {
    typeof update === 'function' && update()
    return
  }
  if (verifyHasRules(rule, authorities)) {
    typeof view === 'function' && view()
    return
  }
})

export const range = R.curry((value, finalValue) => {
  const recursiveRange = (value, array) => {
    if (value > finalValue) return array
    return recursiveRange(R.inc(value), R.append(value, array))
  }

  return recursiveRange(value, [])
})

export const toTwoDigits = R.compose(R.when(R.gt(10), R.concat('0')), R.toString)

export const toFixedTrunc = (x, n = 2) => {
  const v = (typeof x === 'string' ? x : x.toString()).split('.')
  if (n <= 0) return v[0]
  let f = v[1] || ''
  if (f.length > n) return `${v[0]}.${f.substr(0, n)}`
  while (f.length < n) f += '0'
  return `${v[0]}.${f}`
}

export const buildUrl = url => (concat = '') =>
  concat ? R.compose(R.join('/'), R.prepend(url), R.append(R.__, R.always([])))(concat) : url

export const fileDataToBlob = (data, type) => {
  const rawLength = data.length
  const blobArray = new Uint8Array(new ArrayBuffer(rawLength))

  for (let i = 0; i < rawLength; i++) {
    blobArray[i] = data.charCodeAt(i)
  }

  return new Blob([blobArray], { type: `application/${type}` })
}

export const fileBase64ToBlob = (base64, type) => {
  const raw = window.atob(base64)
  return fileDataToBlob(raw, type)
}

export const getBase64 = file =>
  new Promise((resolve, reject) => {
    const reader = new FileReader()
    reader.readAsDataURL(file)
    reader.onload = () => resolve(reader.result.substr(reader.result.indexOf('base64,') + 7))
    reader.onerror = error => reject(error)
  })

export const currencyToNumber = R.compose(
  Number,
  R.replace(',', '.'),
  R.replace('.', ''),
  R.replace(' ', ''),
  R.replace('R$', '')
)

export const percentCommaToPoint = R.compose(Number, R.replace(',', '.'), R.replace('.', ''), R.replace(' ', ''))

export const floatToComma = R.compose(R.replace('.', ','), R.toString)

export const excludeZeros = R.compose(
  //exclui somente os zeros iniciais
  R.replace(/^[0|\D]*/, '')
)

export const formatStatus = contratos => {
  if (contratos) {
    contratos.forEach(contrato => {
      const status = contrato.status

      if (status === 'AG_DEFERIMENTO' && contrato.idContratoCompra) {
        if (contrato.idConvenio === contrato.idConvenioContratoCompra) {
          contrato.status = `${status}_REFIN`
        } else {
          contrato.status = `${status}_PORTABILIDADE`
        }
      }
    })
  }
}

export const isValidDate = date => date instanceof Date && !isNaN(date)

export const removeSlashAndReverseFolhaPrimeiroDesconto = folha => {
  if (folha instanceof Date) {
    folha = dateFnsFormat(folha, 'MM/yyyy')
  }

  const pieces = folha.split('/')
  return pieces[1] + pieces[0]
}

export const openPDF = (arquivo, fileName) => {
  const fileNameProp = !!fileName ? fileName : 'comprovante.pdf'
  const blob = fileBase64ToBlob(arquivo, 'pdf')
  if (window.navigator && window.navigator.msSaveOrOpenBlob) {
    return window.navigator.msSaveOrOpenBlob(blob, fileNameProp)
  }

  return downloadPDF(fileNameProp, arquivo)
}

export const fileToBase64 = file =>
  new Promise(resolve => {
    var reader = new FileReader()
    reader.onload = R.compose(resolve, R.nth(1), R.split(','), R.path(['target', 'result']))
    reader.readAsDataURL(file)
  })

export const roundTwoDecimals = value => Math.round(value * 100) / 100

export const yearMounthToMonthYear = date => `${date.toString().substr(4, 2)}/${date.toString().substr(0, 4)}`

export const withDefaultProps = (prop, fnValue) =>
  branch(
    props => !props[prop],
    withProps(props => ({ [prop]: fnValue(props) }))
  )

export const withListProps = R.compose(
  withState('totalElements', 'setTotalElements', 0),
  withState('currentPage', 'setCurrentPage', 0),
  withState('searchValue', 'setSearchValue', '')
)

export const createFolha = (month, year) => `${year}${toTwoDigits(month)}`

export const folhaStringToNumber = folha => {
  if (!folha) return null
  const match = String(folha)
    .trim()
    .match(/^(\d{1,2})\/(\d{4})$/)
  if (!match) return null
  const month = parseInt(match[1], 10)
  if (month < 1 || month > 12) return null
  return parseInt(`${match[2]}${String(month).padStart(2, '0')}`, 10)
}

export const formatFolhaToUser = date => (date ? `${date.toString().substr(4, 2)}/${date.toString().substr(0, 4)}` : '')

export const formatFolhaByDateToUser = (month, year) => `${month}/${year}`

export const fullYearMonthToMonth = fullYearMonth => {
  const month = fullYearMonth.toString().substr(4, 2)

  return Number(month)
}

export const formatCnpjToNumber = R.compose(
  String,
  R.replace('-', ''),
  R.replace('/', ''),
  R.replace('.', ''),
  R.replace('.', '')
)

export const formatCnpj = cnpj => {
  if (!cnpj) return ''
  const value = String(cnpj)
  if (value.length !== 14) return value
  return value.replace(/^(.{2})(.{3})(.{3})(.{4})(.{2})$/, '$1.$2.$3/$4-$5')
}

const CNPJ_PATTERN = /^[A-Z0-9]{12}\d{2}$/
const CNPJ_FIRST_WEIGHTS = [5, 4, 3, 2, 9, 8, 7, 6, 5, 4, 3, 2]
const CNPJ_SECOND_WEIGHTS = [6, ...CNPJ_FIRST_WEIGHTS]

const cnpjCheckDigit = (base, weights) => {
  const sum = base
    .split('')
    .reduce((total, character, index) => total + (character.charCodeAt(0) - 48) * weights[index], 0)
  const remainder = sum % 11
  return remainder < 2 ? 0 : 11 - remainder
}

/** Validates a numeric or alphanumeric CNPJ (NT COCAD/SUARA/RFB 49/2024), with or without mask. */
// Only the unmasked form or the full mask XX.XXX.XXX/XXXX-XX is accepted; any other character makes it invalid.
const CNPJ_INPUT_PATTERN = /^(?:[A-Za-z0-9]{14}|[A-Za-z0-9]{2}\.[A-Za-z0-9]{3}\.[A-Za-z0-9]{3}\/[A-Za-z0-9]{4}-[A-Za-z0-9]{2})$/

export const isValidCnpj = value => {
  const input = String(value || '').trim()
  if (!CNPJ_INPUT_PATTERN.test(input)) return false

  const cnpj = input.replace(/[^A-Za-z0-9]/g, '').toUpperCase()
  if (!CNPJ_PATTERN.test(cnpj) || /^(.)\1+$/.test(cnpj)) return false

  const firstDigit = cnpjCheckDigit(cnpj.slice(0, 12), CNPJ_FIRST_WEIGHTS)
  const secondDigit = cnpjCheckDigit(cnpj.slice(0, 12) + firstDigit, CNPJ_SECOND_WEIGHTS)
  return cnpj.slice(12) === `${firstDigit}${secondDigit}`
}

export const unmaskCnpj = value =>
  String(value || '')
    .replace(/[^A-Za-z0-9]/g, '')
    .toUpperCase()

export const formatCpfToNumber = cpf => (cpf ? String(cpf).replace(/\D/g, '') : '')

export const formatTelefoneToNumber = R.compose(
  String,
  R.replace('-', ''),
  R.replace(' ', ''),
  R.replace(' ', ''),
  R.replace(')', ''),
  R.replace('(', '')
)

export const zipCodeToNumber = R.compose(String, R.replace('-', ''))

export function restrictToNumberAndDots(value) {
  return value.replace(/[^0-9\\.]/g, '')
}

export function restrictToValidRgDigits(value) {
  if (value) {
    return value.replace(/[^0-9]/g, '')
  }
}

export function validaCpf(value) {
  var cpf = formatCpfToNumber(value)
  if (cpf === '') return false
  // Elimina CPFs invalidos conhecidos
  if (
    cpf.length !== 11 ||
    cpf === '00000000000' ||
    cpf === '11111111111' ||
    cpf === '22222222222' ||
    cpf === '33333333333' ||
    cpf === '44444444444' ||
    cpf === '55555555555' ||
    cpf === '66666666666' ||
    cpf === '77777777777' ||
    cpf === '88888888888' ||
    cpf === '99999999999'
  )
    return false
  // Valida 1o digito
  let add = 0
  let rev = 0

  for (var i = 0; i < 9; i++) add += parseInt(cpf.charAt(i)) * (10 - i)
  rev = 11 - (add % 11)
  if (rev === 10 || rev === 11) rev = 0
  if (rev !== parseInt(cpf.charAt(9))) return false
  // Valida 2o digito
  add = 0
  for (i = 0; i < 10; i++) add += parseInt(cpf.charAt(i)) * (11 - i)
  rev = 11 - (add % 11)
  if (rev === 10 || rev === 11) rev = 0
  if (rev !== parseInt(cpf.charAt(10))) return false
  return true
}

export function validaTelefone(value) {
  var telefone = formatTelefoneToNumber(value)

  return telefone.length >= 10
}

export const replaceNewLines = R.when(hasValue, R.replace(/\n/g, '<br />'))

export const escapeHtml = str => {
  if (!str) return ''
  return str
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/\n/g, '<br/>')
}

export function getSecondPart(element, excludeChar) {
  if (element.includes(excludeChar)) {
    return element.split(excludeChar)[1]
  } else {
    return element
  }
}

export function getMinYearAllowed() {
  const minimumDate = new Date(2000, 0, 1)
  return minimumDate
}
export function getMaxYearAllowed() {
  const maximumYear = new Date().getFullYear() + 5
  const maximumDate = new Date(maximumYear, 11, 31)
  return maximumDate
}

export const UserNavigationBlocker = (message, callback) => {
  const node = document.createElement('div')

  node.setAttribute('csg-confirm-modal', '')

  const shouldOpen = answer => {
    ReactDOM.unmountComponentAtNode(node)
    callback(answer)
  }

  const handleConfirm = callbackState => {
    ReactDOM.unmountComponentAtNode(node)
    callback(callbackState)
  }

  const handleCancel = () => {
    ReactDOM.unmountComponentAtNode(node)
    callback()
  }

  document.body.appendChild(node)

  ReactDOM.render(
    <CsgModalConfirmation open={shouldOpen} message={message} onConfirm={handleConfirm} onCancel={handleCancel} />,
    node
  )
}

export const areTheyTheSameObjects = (firstObject, secondObject) => {
  const firstObjectKey = Object.keys(firstObject)
  const secondObjectKey = Object.keys(secondObject)
  if (firstObjectKey.length !== secondObjectKey.length) {
    return false
  }
  for (let key of firstObjectKey) {
    if (firstObject[key] !== secondObject[key]) {
      return false
    }
  }
  return true
}

export function isEmptyOrNil(value) {
  return value === '' || value === null || value === undefined
}

export const downloadBlob = (filename, blob) => {
  const url = window.URL.createObjectURL(blob)
  const a = document.createElement('a')
  a.href = url
  a.download = filename
  document.body.appendChild(a)
  a.click()
  a.remove()
  window.URL.revokeObjectURL(url)
}

export function calculateCheckDigitModulo10(block) {
  let sum = 0
  let multiplier = 2
  for (let i = block.length - 1; i >= 0; i--) {
    let result = parseInt(block[i]) * multiplier
    if (result > 9) result -= 9
    sum += result
    multiplier = multiplier === 2 ? 1 : 2
  }
  const remainder = sum % 10
  return remainder === 0 ? 0 : 10 - remainder
}

function calculateCheckDigitModulo11(field) {
  let sum = 0
  let multiplier = 2
  for (let i = field.length - 1; i >= 0; i--) {
    sum += parseInt(field[i]) * multiplier
    multiplier = multiplier === 9 ? 2 : multiplier + 1
  }
  const remainder = sum % 11
  if (remainder === 0 || remainder === 1) return 1
  return 11 - remainder
}

export function extractValueFromDigitableLine(line) {
  const digits = (line || '').replace(/\D/g, '')
  if (digits.length !== 47) return null
  const raw = parseInt(digits.substring(37, 47), 10)
  if (isNaN(raw) || raw === 0) return null
  return (raw / 100).toFixed(2)
}

export function validateDigitableLine(line) {
  const digits = (line || '').replace(/\D/g, '')
  if (digits.length !== 47 && digits.length !== 48) return false

  try {
    const field1 = digits.substring(0, 9)
    const field2 = digits.substring(10, 20)
    const field3 = digits.substring(21, 31)

    const checkDigit1 = parseInt(digits[9])
    const checkDigit2 = parseInt(digits[20])
    const checkDigit3 = parseInt(digits[31])

    if (calculateCheckDigitModulo10(field1) !== checkDigit1) return false
    if (calculateCheckDigitModulo10(field2) !== checkDigit2) return false
    if (calculateCheckDigitModulo10(field3) !== checkDigit3) return false

    const generalCheckDigit = parseInt(digits[32])
    const barCode =
      digits.substring(0, 3) +
      digits.substring(3, 4) +
      digits.substring(33, 37) +
      digits.substring(37, 47) +
      digits.substring(4, 9) +
      digits.substring(10, 20) +
      digits.substring(21, 31)

    if (calculateCheckDigitModulo11(barCode) !== generalCheckDigit) return false

    return true
  } catch {
    return false
  }
}
